import { Button } from "@/components/ui/button";
import { useEvents } from "@/features/calendar/hooks/use-events";
import { useSchedules } from "@/features/calendar/hooks/use-schedules";
import { useTimeOffs } from "@/features/calendar/hooks/use-timeoffs";
import type { CalendarEvent } from "@/features/calendar/types";
import { CALENDAR_REMINDER_STATUSES } from "@/features/reminders/display";
import {
  useReminders,
  useRemindersEnabled,
} from "@/features/reminders/hooks/use-reminders";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import {
  addDays,
  addWeeks,
  endOfWeek,
  startOfWeek,
  subDays,
  subWeeks,
} from "date-fns";
import { useMemo, useState } from "react";
import { useTitle } from "@/features/utils/use-title";
import { CalendarDayView } from "./components/calendar-day-view";
import { CalendarFilters } from "./components/calendar-filters";
import { CalendarWeekView } from "./components/calendar-week-view";
import { EventDialog } from "./components/event-dialog";

export function CalendarPage() {
  const { t } = useTranslation("calendar");
  useTitle(t("title"));
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null,
  );
  const [defaultStartTime, setDefaultStartTime] = useState<
    number | undefined
  >();
  const [defaultEndTime, setDefaultEndTime] = useState<number | undefined>();
  const [isMobileView, setIsMobileView] = useState(false);

  const { data: schedules = [] } = useSchedules();
  const { data: timeOffs = [] } = useTimeOffs();

  // Fetch events for the current week/day
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });

  const timeMin = weekStart.getTime() - 7 * 24 * 60 * 60 * 1000; // Fetch extra week before for better UX when dragging events
  const timeMax = weekEnd.getTime() + 7 * 24 * 60 * 60 * 1000; // Fetch extra week after for better UX when dragging events

  const { data: events = [] } = useEvents({ timeMin, timeMax });

  // Follow-ups the agent planned with customers. Same window as the events, and only
  // fetched at all when the tenant turned the reminders module on.
  const remindersEnabled = useRemindersEnabled();
  const { data: reminders = [] } = useReminders({
    from: timeMin,
    to: timeMax,
    status: CALENDAR_REMINDER_STATUSES,
    enabled: remindersEnabled,
  });

  // Detect mobile view
  useMemo(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // What the grid shows, in the header: the week (Monday to Sunday, as the
  // week view lays it out) or, on a phone, the day.
  const { format } = useDateFormat();
  const shownWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const shownWeekEnd = addDays(shownWeekStart, 6);
  const rangeLabel = isMobileView
    ? format(currentDate, "EEEE d MMMM yyyy")
    : `${format(
        shownWeekStart,
        shownWeekStart.getFullYear() === shownWeekEnd.getFullYear()
          ? "d MMM"
          : "d MMM yyyy",
      )} – ${format(shownWeekEnd, "d MMM yyyy")}`;

  const handlePrevious = () => {
    if (isMobileView) {
      setCurrentDate((prev) => subDays(prev, 1));
    } else {
      setCurrentDate((prev) => subWeeks(prev, 1));
    }
  };

  const handleNext = () => {
    if (isMobileView) {
      setCurrentDate((prev) => addDays(prev, 1));
    } else {
      setCurrentDate((prev) => addWeeks(prev, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleSlotClick = (startTime: number, endTime: number) => {
    setDefaultStartTime(startTime);
    setDefaultEndTime(endTime);
    setSelectedEvent(null);
    setEventDialogOpen(true);
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDefaultStartTime(undefined);
    setDefaultEndTime(undefined);
    setEventDialogOpen(true);
  };

  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
    setDefaultStartTime(undefined);
    setDefaultEndTime(undefined);
  };

  return (
    <>
      {/* Full height and flush with the application frame, like the calls
          history: a week grid is read top to bottom, and the page title bar
          above it only pushed the working hours below the fold. One toolbar
          carries what the header held — the shown week, its arrows, the
          filters. The negative margins undo the layout's `px-4 md:px-8`. */}
      <div className="-mx-4 flex h-[calc(100dvh-3rem)] flex-col overflow-hidden bg-background md:-mx-8 md:h-[calc(100dvh-1rem)] md:rounded-lg">
        <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2 md:px-4">
          <Button variant="outline" size="sm" onClick={handleToday}>
            {t("today")}
          </Button>
          <div className="flex">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={handlePrevious}
              aria-label={t(isMobileView ? "previousDay" : "previousWeek")}
            >
              <CaretLeftIcon className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={handleNext}
              aria-label={t(isMobileView ? "nextDay" : "nextWeek")}
            >
              <CaretRightIcon className="size-4" />
            </Button>
          </div>
          <h1 className="order-last min-w-0 basis-full truncate text-base font-semibold first-letter:uppercase md:order-none md:basis-auto md:text-lg">
            {rangeLabel}
          </h1>
          <div className="ml-auto">
            <CalendarFilters
              selectedMembers={selectedMembers}
              onMembersChange={setSelectedMembers}
              selectedServices={selectedServices}
              onServicesChange={setSelectedServices}
              isMobile={isMobileView}
            />
          </div>
        </div>

        <div className="min-h-0 flex-1">
          {isMobileView ? (
            <CalendarDayView
              currentDate={currentDate}
              events={events}
              reminders={reminders}
              schedules={schedules}
              timeOffs={timeOffs}
              onSlotClick={handleSlotClick}
              onEventClick={handleEventClick}
              selectedMembers={selectedMembers}
              selectedServices={selectedServices}
            />
          ) : (
            <CalendarWeekView
              currentDate={currentDate}
              events={events}
              reminders={reminders}
              schedules={schedules}
              timeOffs={timeOffs}
              onSlotClick={handleSlotClick}
              onEventClick={handleEventClick}
              selectedMembers={selectedMembers}
              selectedServices={selectedServices}
            />
          )}
        </div>
      </div>

      {/* Event Dialog */}
      <EventDialog
        open={eventDialogOpen}
        onOpenChange={handleEventDialogClose}
        event={selectedEvent}
        defaultStartTime={defaultStartTime}
        defaultEndTime={defaultEndTime}
      />
    </>
  );
}
